import {PrismicRichText} from '@prismicio/react'
import type {ProductTourSectionSliceDefaultPrimaryDialogItem} from 'prismicio-types'
import {useCallback, useContext, useEffect, useRef} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import type {Resolver, ResolverResult} from 'react-hook-form'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {ButtonV2} from 'src/common/ButtonV2'
import Dialog from 'src/common/Dialog'
import {findFieldError} from 'src/common/helpers/findFieldError'
import {useGetConfig} from 'src/common/hooks/useGetConfig'
import {TextInput} from 'src/common/TextInput'
import {DialogContext} from 'src/context/DialogContext'
import {useLeadSubmission} from '../hooks/useLeadSubmission'

type FormValues = {
  email: string
}

type LeadGenerationFormProps = {
  content: ProductTourSectionSliceDefaultPrimaryDialogItem
  pageUid?: string
  callbackAfterSubmit?: () => void
}

export const LeadGenerationForm = ({content, pageUid, callbackAfterSubmit}: LeadGenerationFormProps) => {
  const config = useGetConfig()
  const {i18n} = useTranslation()
  const {setIsDialogOpen} = useContext(DialogContext)!
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)
  const {executeRecaptcha} = useGoogleReCaptcha()

  const formResolver: Resolver<FormValues> = async (values): Promise<ResolverResult<FormValues>> => {
    if (values.email?.trim()) {
      return {values, errors: {}}
    }

    return {
      values: {},
      errors: {
        email: {
          type: 'required',
          message: content.field_required_message || 'Email is required',
        },
      },
    }
  }

  const {register, handleSubmit, formState, setError, getValues} = useForm<FormValues>({
    resolver: formResolver,
  })

  const {submitLead, apiError} = useLeadSubmission({
    pageUid,
    errorMessage: content.error_message || '',
    onSuccess: callbackAfterSubmit,
    onError: (message) => {
      setError('email', {
        type: 'validate',
        message,
      })
    },
    tracking: {
      recaptchaAction: 'product_tour_lead',
      formName: 'product-tour-lead-form',
      intent: 'product-tour',
      location: 'product-tour-section',
    },
    useSingletonRecaptcha: false,
    executeRecaptchaFromWrapper: executeRecaptcha,
  })

  const onSubmit = handleSubmit(async (data) => {
    await submitLead(data.email)
  })

  //check for captcha error and show reCaptcha
  useEffect(() => {
    if (!apiError || !apiError.response?.data?.fieldErrors) {
      return
    }

    const captchaError = findFieldError(apiError.response.data.fieldErrors, 'captchaToken')

    if (captchaError) {
      setIsDialogOpen(true)
    }
  }, [apiError, setIsDialogOpen])

  const handleChallengeResolved = useCallback(
    (token: string | null) => {
      if (!token) {
        return
      }

      submitLead(getValues().email, token)
      setIsDialogOpen(false)
      recaptchaRef.current?.reset()
    },
    [getValues, setIsDialogOpen, submitLead],
  )

  return (
    <>
      <div className="flex w-[450px] flex-col items-center gap-3 rounded-lg bg-moss-white p-12">
        <h3 className="text-tg-body-l font-medium">{content.title}</h3>
        <div>
          <PrismicRichText
            field={content.description}
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-m text-center font-medium text-moss-grey-400">{children}</p>
              ),
            }}
          />
        </div>
        <form onSubmit={onSubmit} className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-1">
            <TextInput
              register={register}
              field={{
                name: 'email',
                type: 'email',
                placeholder: content.email_field_placeholder || '',
              }}
              errors={formState.errors ? Object.keys(formState.errors).map((key) => ({fieldName: key})) : null}
              className="!h-10"
            />
            {formState.errors?.email && (
              <p className="text-tg-body-s text-ui-red-450">{formState.errors.email.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <ButtonV2
              type="submit"
              variant="primary"
              size="small"
              disabled={formState.isSubmitting}
              trackingData={{
                mktgId: 'product-tour-submit-button',
                dataType: 'submit-button',
                location: 'product-tour-section',
                pageName: pageUid || '',
              }}
              newTrackingProps={{
                interaction: {
                  element: 'product-tour-submit-button',
                  elementText: content.submit_button.text || '',
                  location: 'product-tour-section',
                },
                pageType: pageUid,
              }}
            >
              {content.submit_button.text}
            </ButtonV2>
            <PrismicRichText
              field={content.disclaimer}
              components={{
                paragraph: ({children}) => <p className="text-body-xs text-grey-300">{children}</p>,
                hyperlink: ({children, node}) => (
                  <a
                    href={node.data.url}
                    target="_blank"
                    className="text-body-xs text-moss-grey-400 underline"
                    rel="noreferrer"
                  >
                    {children}
                  </a>
                ),
              }}
            />
          </div>
        </form>
      </div>
      <Dialog dismissible>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={config.RECAPTCHA_V2}
          onChange={handleChallengeResolved}
          hl={i18n.language}
        />
      </Dialog>
    </>
  )
}
